import { Lock, ArrowRight, Scale, Users, Settings, Search, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackCTA } from "@/lib/analytics";

const HERO_FEATURES = [
  { id: "simuladores", text: "Simuladores e imágenes de pacientes" },
  { id: "proteccion", text: "Protección de datos y consentimiento" },
  { id: "marketing", text: "Marketing automatizado y chatbots" },
  { id: "gobernanza", text: "Gobernanza interna y supervisión" },
];

const BENEFITS = [
  { id: "riesgo", icon: Scale, color: "blue", title: "Menos riesgo legal", desc: "Detectamos puntos críticos antes de que se conviertan en reclamaciones o sanciones." },
  { id: "confianza", icon: Users, color: "green", title: "Más confianza del paciente", desc: "Claridad en consentimiento, transparencia y límites de las simulaciones." },
  { id: "control", icon: Settings, color: "purple", title: "Más control interno", desc: "Políticas, checklist y validación humana en usos sensibles de la IA." },
];

const FEATURE_BAR_ITEMS = [
  { id: "auditoria", icon: Search, text: "Auditoría del uso real de IA" },
  { id: "legal", icon: Scale, text: "Enfoque legal + operativo" },
  { id: "clinicas", icon: Building2, text: "Pensado para clínicas privadas" },
  { id: "integrable", icon: Settings, text: "Integrable con tus procesos" },
];

const STATS = [
  { id: "herramientas", value: "+40", label: "Herramientas IA revisadas habitualmente" },
  { id: "rgpd", value: "RGPD", label: "Reglamento general de protección de datos (UE)" },
  { id: "aiact", value: "AI Act", label: "Reglamento europeo de IA en vigor desde 2024" },
  { id: "lopdgdd", value: "LOPDGDD", label: "Ley orgánica de protección de datos española" },
];

export function HeroSection({ scrollToSection }) {
  return (
    <>
      <section className="hero-section" data-testid="hero-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="animate-fade-in-up">
              <div className="eyebrow mb-6">
                <span>⚡</span>
                <span>Auditoría legal y técnica de IA para clínicas estéticas</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-extrabold text-white leading-[1.1] mb-6">
                Usa <span className="text-gradient-blue">inteligencia artificial</span> en tu clínica con seguridad jurídica
              </h1>
              
              <p className="text-gray-400 text-lg mb-8 leading-relaxed max-w-xl">
                Auditamos el uso de IA en clínicas de medicina estética y cirugía estética para detectar riesgos en protección de datos, consentimiento informado, imágenes de pacientes, marketing automatizado y gobernanza interna.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-10">
                <Button onClick={() => { trackCTA("hero_diagnostico"); scrollToSection('diagnostico'); }} className="btn-primary text-base" data-testid="hero-cta-button">
                  <Lock className="w-4 h-4" />
                  Solicitar diagnóstico
                </Button>
                <Button onClick={() => scrollToSection('servicio')} variant="outline" className="btn-secondary text-base" data-testid="hero-secondary-button">
                  <ArrowRight className="w-4 h-4" />
                  Ver qué incluye
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {HERO_FEATURES.map((item) => (
                  <div key={item.id} className="flex items-center gap-2 text-gray-300 text-sm">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    {item.text}
                  </div>
                ))}
              </div>
            </div>

            <div className="hero-card animate-slide-in-right stagger-2" data-testid="hero-benefits-card">
              <h3 className="hero-card-title">¿Qué conseguimos juntos?</h3>
              <p className="hero-card-desc">
                Convertimos un uso desordenado de la IA en un sistema documentado, revisable y defendible ante pacientes, organismos reguladores y terceros.
              </p>
              
              <div className="space-y-3">
                {BENEFITS.map((b) => (
                  <div key={b.id} className="benefit-item">
                    <div className="flex gap-4">
                      <div className={`benefit-icon ${b.color}`}>
                        <b.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold mb-1">{b.title}</h4>
                        <p className="text-gray-400 text-sm">{b.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="features-bar" data-testid="features-bar">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {FEATURE_BAR_ITEMS.map((item) => (
              <div key={item.id} className="feature-item">
                <item.icon className="w-5 h-5 text-blue-400" />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section className="stats-section" data-testid="stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((stat) => (
              <div key={stat.id} className="stat-item">
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
